/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { FhirPredefinedWidget } from '../algorithm/types';
import { createRandomId } from '../utils';

import { DynamedGuideMapping, guideToTestDataMapping } from './test_data/mapping';

export const DYNAMED_TAB_LABEL = 'Dynamed';
export const DYNAMED_TAB_VALUE = 'dynamed';
export const DYNAMED_PAGE_ID = 'DYNAMED';

export type DynamedSection = {
  anchor?: string;
  content: string;
  name: string;
};

export function getDynamedGuide(guideName?: string | null): DynamedGuideMapping | null {
  if (!guideName) return null;
  return guideToTestDataMapping[guideName] || null;
}

export function hasDynamedGuide(guideName?: string | null): boolean {
  return !!getDynamedGuide(guideName);
}

/** Load Dynamed guide sections. For now this is just hardcoded dummy data.
 *  Returns ordered HTML sections of the dynamed guide content.
 */
export async function loadDynamedGuideSections(guideName: string): Promise<DynamedSection[] | null> {
  const guide = getDynamedGuide(guideName);
  if (!guide) return null;

  const sections = await Promise.all(
    guide.sections.map(async (section) => {
      const response = await fetch(`/dynamed_test_data/${section.filename}`);
      if (!response.ok) {
        console.error(`Failed to load test data for guide ${guideName} from file ${section.filename}`);
        return null;
      }

      return {
        content: await response.text(),
        name: section.name,
      };
    })
  );

  const loadedSections = sections.filter((section): section is DynamedSection => !!section?.content);
  return loadedSections.length > 0 ? loadedSections : null;
}

export type DynamedKeywordMatch = {
  keyword: string;
  predefinedWidgetIds: string[];
  negativeKeywordsByWidgetId?: Record<string, string[]>;
};

export type DynamedTextMatch = {
  index: number;
  length: number;
  predefinedWidgetIds: string[];
};

export const REALTIME_WIDGET_MIN_DISTANCE = 2500;

export type KeywordMatchDistanceState = {
  lastWidgetMatchEnd: Map<string, number>;
  textOffset: number;
};

function normalizeKeyword(keyword: string): string {
  return keyword.trim().replace(/\s+/g, ' ');
}

function normalizeKeywordLookup(keyword: string): string {
  return normalizeKeyword(keyword).toLowerCase();
}

function isWordChar(char: string | undefined): boolean {
  return !!char && /[A-Za-z0-9]/.test(char);
}

function canMatchAt(text: string, index: number, keyword: string): boolean {
  const before = text[index - 1];
  const after = text[index + keyword.length];
  const startsWithWord = isWordChar(keyword[0]);
  const endsWithWord = isWordChar(keyword[keyword.length - 1]);

  if (startsWithWord && isWordChar(before)) return false;
  if (endsWithWord && isWordChar(after)) return false;
  return true;
}

function isMatchExcludedForWidget(
  text: string,
  matchIndex: number,
  matchLength: number,
  negativeKeywords: string[] | undefined
): boolean {
  if (!negativeKeywords?.length) return false;

  return negativeKeywords.some((negativeKeyword) => {
    let negativeIndex = text.indexOf(negativeKeyword);
    while (negativeIndex !== -1) {
      const negativeEnd = negativeIndex + negativeKeyword.length;
      if (
        canMatchAt(text, negativeIndex, negativeKeyword) &&
        negativeIndex <= matchIndex &&
        negativeEnd >= matchIndex + matchLength
      ) {
        return true;
      }
      negativeIndex = text.indexOf(negativeKeyword, negativeIndex + 1);
    }
    return false;
  });
}

export function findKeywordMatchesInText(
  text: string,
  keywordMatches: DynamedKeywordMatch[],
  distanceState?: KeywordMatchDistanceState,
  minimumWidgetDistance = REALTIME_WIDGET_MIN_DISTANCE
): DynamedTextMatch[] {
  const lowerCasedText = text.toLowerCase();
  const matches: DynamedTextMatch[] = [];
  let cursor = 0;

  while (cursor < text.length) {
    let nextMatch:
      | {
          index: number;
          keyword: string;
          predefinedWidgetIds: Set<string>;
        }
      | undefined;

    for (const match of keywordMatches) {
      const index = lowerCasedText.indexOf(match.keyword.toLowerCase(), cursor);
      if (index === -1) continue;
      if (!canMatchAt(text, index, match.keyword)) continue;

      if (nextMatch && index === nextMatch.index && match.keyword.length === nextMatch.keyword.length) {
        nextMatch.predefinedWidgetIds = new Set([...nextMatch.predefinedWidgetIds, ...match.predefinedWidgetIds]);
        continue;
      }

      if (
        !nextMatch ||
        index < nextMatch.index ||
        (index === nextMatch.index && match.keyword.length > nextMatch.keyword.length)
      ) {
        nextMatch = {
          index,
          keyword: match.keyword,
          predefinedWidgetIds: new Set(match.predefinedWidgetIds),
        };
      }
    }

    if (!nextMatch) break;

    const absoluteIndex = (distanceState?.textOffset || 0) + nextMatch.index;
    const matchingKeyword = keywordMatches.find((match) => match.keyword === nextMatch.keyword);
    const predefinedWidgetIds = [...nextMatch.predefinedWidgetIds].filter((widgetId) => {
      const negativeKeywords = matchingKeyword?.negativeKeywordsByWidgetId?.[widgetId];
      return !isMatchExcludedForWidget(lowerCasedText, nextMatch.index, nextMatch.keyword.length, negativeKeywords);
    });
    const hasEligibleWidget = predefinedWidgetIds.some((widgetId) => {
      const lastMatchEnd = distanceState?.lastWidgetMatchEnd.get(widgetId);
      return lastMatchEnd === undefined || absoluteIndex - lastMatchEnd >= minimumWidgetDistance;
    });

    if (hasEligibleWidget) {
      matches.push({
        index: nextMatch.index,
        length: nextMatch.keyword.length,
        predefinedWidgetIds,
      });
      predefinedWidgetIds.forEach((widgetId) => {
        distanceState?.lastWidgetMatchEnd.set(widgetId, absoluteIndex + nextMatch.keyword.length);
      });
    }
    cursor = nextMatch.index + nextMatch.keyword.length;
  }

  return matches;
}

/**
 * Builds the searchable keyword table used to add realtime FHIR contextualization to DynaMed HTML.
 * Each non-WIP, non-deleted predefined widget contributes its comma-separated search terms; duplicate
 * keywords are merged so one matched term can point at multiple widget ids, then longer keywords are
 * sorted first so overlapping matches prefer the most specific phrase.
 */
export function buildKeywordMatches(predefinedWidgets: FhirPredefinedWidget[]): DynamedKeywordMatch[] {
  const matchesByKeyword = new Map<
    string,
    { keyword: string; predefinedWidgetIds: Set<string>; negativeKeywordsByWidgetId: Record<string, string[]> }
  >();

  predefinedWidgets.forEach((widget) => {
    if (!widget.id || !widget.searchTerms || widget.wip || widget.deleted) return;
    widget.searchTerms
      .split(',')
      .map(normalizeKeyword)
      .filter((keyword) => keyword.length > 2)
      .forEach((keyword) => {
        const normalizedKeyword = normalizeKeywordLookup(keyword);
        const existing = matchesByKeyword.get(normalizedKeyword) || {
          keyword,
          predefinedWidgetIds: new Set<string>(),
          negativeKeywordsByWidgetId: {},
        };
        existing.predefinedWidgetIds.add(widget.id);
        existing.negativeKeywordsByWidgetId[widget.id] = (widget.negativeSearchTerms || '')
          .split(',')
          .map(normalizeKeywordLookup)
          .filter((negativeKeyword) => negativeKeyword.length > 2);
        matchesByKeyword.set(normalizedKeyword, existing);
      });
  });

  return [...matchesByKeyword.values()]
    .map(({ keyword, predefinedWidgetIds, negativeKeywordsByWidgetId }) => {
      const hasNegativeKeywords = Object.values(negativeKeywordsByWidgetId).some((keywords) => keywords.length > 0);
      return {
        keyword,
        predefinedWidgetIds: [...predefinedWidgetIds],
        ...(hasNegativeKeywords ? { negativeKeywordsByWidgetId } : {}),
      };
    })
    .sort((a, b) => b.keyword.length - a.keyword.length);
}

function contextualizeTextNode(
  textNode: Text,
  keywordMatches: DynamedKeywordMatch[],
  distanceState?: KeywordMatchDistanceState
) {
  const originalText = textNode.textContent;
  if (!originalText) return;

  const doc = textNode.ownerDocument;
  const fragment = doc.createDocumentFragment();
  let cursor = 0;

  const matches = findKeywordMatchesInText(originalText, keywordMatches, distanceState);
  if (distanceState) distanceState.textOffset += originalText.length;
  for (const match of matches) {
    if (match.index > cursor) {
      fragment.appendChild(doc.createTextNode(originalText.slice(cursor, match.index)));
    }

    const matchedText = originalText.slice(match.index, match.index + match.length);
    const fhirNode = doc.createElement('fhir');
    fhirNode.setAttribute('id', `f${createRandomId()}`);
    fhirNode.setAttribute('data-fhir-widget-id', match.predefinedWidgetIds.join(';'));
    fhirNode.textContent = matchedText;
    fragment.appendChild(fhirNode);

    cursor = match.index + match.length;
  }

  if (matches.length === 0) return;

  if (cursor < originalText.length) {
    fragment.appendChild(doc.createTextNode(originalText.slice(cursor)));
  }

  textNode.replaceWith(fragment);
}

export function contextualizeDynamedHtml(
  content: string,
  predefinedWidgets: FhirPredefinedWidget[],
  options?: { includeExistingFhir?: boolean; pageName?: string }
): string {
  if (!content || predefinedWidgets.length === 0 || options?.pageName?.trim().toLowerCase() === 'resources') {
    return content;
  }

  const keywordMatches = buildKeywordMatches(predefinedWidgets);
  if (keywordMatches.length === 0) return content;

  const doc = document.implementation.createHTMLDocument('');
  doc.body.innerHTML = content;

  if (options?.includeExistingFhir) {
    doc.body.querySelectorAll('fhir').forEach((node) => node.replaceWith(...Array.from(node.childNodes)));
  }

  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parentElement = node.parentElement;
      if (!parentElement) return NodeFilter.FILTER_REJECT;
      if (parentElement.closest('a, script, style, fhir, .GR_List')) {
        return NodeFilter.FILTER_REJECT;
      }
      if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    if (currentNode instanceof Text) {
      textNodes.push(currentNode);
    }
    currentNode = walker.nextNode();
  }

  const distanceState: KeywordMatchDistanceState = { lastWidgetMatchEnd: new Map(), textOffset: 0 };
  textNodes.forEach((textNode) =>
    contextualizeTextNode(
      textNode,
      keywordMatches,
      textNode.parentElement?.closest('action') ? undefined : distanceState
    )
  );
  return doc.body.innerHTML;
}
